import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, MapPin, Phone, CalendarCheck, TrendingUp } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumb, CtaBand } from "@/components/PageBits";
import { useLead } from "@/context/LeadContext";
import { useBooking } from "@/context/BookingContext";
import { SatisfactionBadge } from "@/components/landing/SatisfactionBadge";
import { AnimatedLines } from "@/components/landing/AnimatedLines";
import { findService, findCategory, servicesOfCategory, ZONES, SITE, CONTACT } from "@/data/pages";

export default function ServicePage() {
  const { slug } = useParams();
  const { openForm } = useLead();
  const { openBooking } = useBooking();
  const service = findService(slug);
  if (!service) return <Navigate to="/404" replace />;

  const category = findCategory(service.category);
  const related = servicesOfCategory(service.category).filter((s) => s.slug !== service.slug).slice(0, 3);
  const path = `/${service.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.metaDescription,
    provider: { "@type": "LocalBusiness", "@id": `${SITE.domain}/#business`, name: SITE.name, telephone: CONTACT.phone },
    areaServed: ["Belfort", "Montbéliard", "Héricourt", "Grand-Charmont"].map((name) => ({ "@type": "City", name })),
    ...(service.faq && {
      mainEntityOfPage: {
        "@type": "FAQPage",
        mainEntity: service.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    }),
  };

  return (
    <div data-testid="service-page">
      <Seo title={service.metaTitle} description={service.metaDescription} path={path} image={service.image} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-background border-b border-border">
        <AnimatedLines />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-10 lg:py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Breadcrumb
              items={[
                { label: "Accueil", to: "/" },
                { label: category.name, to: `/${category.slug}` },
                { label: service.name },
              ]}
            />
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink mt-4 leading-[1.05]">
              {service.h1}
            </h1>
            <p className="text-muted-foreground mt-5 text-base sm:text-lg leading-relaxed">{service.intro}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                data-testid="service-book"
                onClick={openBooking}
                className="cta inline-flex items-center gap-2 bg-royal text-white font-extrabold px-7 py-4 rounded-sm hover:bg-royal-hover shadow-lg shadow-royal/30"
              >
                <CalendarCheck className="w-5 h-5" /> Réserver mon créneau
              </button>
              <button
                data-testid="service-cta"
                onClick={() => openForm(service.service)}
                className="cta inline-flex items-center gap-2 border border-ink/20 text-ink font-bold px-7 py-4 rounded-sm hover:bg-ink hover:text-white"
              >
                Obtenir mon devis <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href={CONTACT.phoneHref}
                className="inline-flex items-center gap-2 border border-ink/20 text-ink font-bold px-7 py-4 rounded-sm hover:bg-ink hover:text-white transition-colors"
              >
                <Phone className="w-5 h-5" /> {CONTACT.phone}
              </a>
            </div>
            <div className="mt-5">
              <SatisfactionBadge />
            </div>
          </div>
          <div className="relative overflow-hidden rounded-sm">
            <img src={service.image} alt={service.h1} className="w-full h-[300px] lg:h-[380px] object-cover" />
          </div>
        </div>
      </section>

      {service.highlight && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 pt-14">
          <div
            data-testid="service-highlight"
            className="flex items-start gap-4 bg-ink text-white rounded-sm p-6 md:p-8"
          >
            <TrendingUp className="w-7 h-7 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading font-bold text-lg">Un investissement qui se rentabilise</h2>
              <p className="text-white/80 mt-2 leading-relaxed">{service.highlight}</p>
            </div>
          </div>
        </section>
      )}

      {service.benefits && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight text-ink">
            Ce que comprend la prestation
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {service.benefits.map((b) => (
              <div key={b} className="flex items-start gap-3 bg-marble-light border border-border rounded-sm p-5">
                <Check className="w-5 h-5 text-ink mt-0.5 shrink-0" />
                <span className="text-ink font-medium">{b}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {service.faq && (
        <section className="bg-background border-y border-border">
          <div className="max-w-4xl mx-auto px-5 md:px-8 py-16">
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight text-ink">Questions fréquentes</h2>
            <div className="mt-8 space-y-4">
              {service.faq.map((f) => (
                <div key={f.q} className="bg-white border border-border rounded-sm p-6">
                  <h3 className="font-heading font-bold text-ink">{f.q}</h3>
                  <p className="text-muted-foreground mt-2 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14">
        <h2 className="font-heading font-bold text-xl text-ink flex items-center gap-2">
          <MapPin className="w-5 h-5" /> Zones d'intervention
        </h2>
        <div className="flex flex-wrap gap-3 mt-4">
          {ZONES.map((z) => (
            <Link
              key={z.slug}
              to={`/${z.slug}`}
              className="text-sm font-semibold bg-marble-light border border-border rounded-sm px-4 py-2 hover:border-ink transition-colors"
            >
              {service.name} à {z.city}
            </Link>
          ))}
        </div>

        {related.length > 0 && (
          <>
            <h2 className="font-heading font-bold text-xl text-ink mt-12">Prestations liées</h2>
            <div className="grid sm:grid-cols-3 gap-4 mt-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/${r.slug}`}
                  className="lift group bg-white border border-border rounded-sm p-5 hover:border-ink"
                >
                  <span className="font-heading font-bold text-ink">{r.name}</span>
                  <ArrowRight className="w-4 h-4 mt-2 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      <CtaBand
        onReserve={() => openForm(service.service)}
        onBook={openBooking}
        title="Un projet ou une urgence ?"
        subtitle="Devis sous 24h pour les urgences, 48h pour les demandes classiques."
      />
    </div>
  );
}
